import { useCallback, useEffect, useMemo } from 'react';
import { usePortalQuery } from '../../../hooks/usePortalQuery';
import { useMapContext } from '../../../context/MapContext';
import { getSelectedDriver } from '../../../services/DeliveryOperationsPortalService';
import { usePortalAccess } from '../../../hooks/usePortalAccess';

const buildMarkers = (selectedDriver) => {
  if (selectedDriver)
    return [
      {
        id: selectedDriver.id,
        status: selectedDriver.status,
        text: `${selectedDriver?.firstName} ${selectedDriver?.lastName}`,
        latitude: selectedDriver?.location?.latitude,
        longitude: selectedDriver?.location?.longitude,
      },
    ];
  return [];
};

export const useDriverPageMap = ({
  selectedDriver,
  isDesktop,
  vh,
  classes,
}) => {
  const markers = useMemo(() => buildMarkers(selectedDriver), [selectedDriver]);

  const { updateMarkers } = useMapContext();

  const onFirstFetch = useCallback(
    (result) => {
      updateMarkers(buildMarkers(result), {
        centerAfterUpdate: true,
        shouldZoom: true,
      });
    },
    [updateMarkers]
  );

  useEffect(() => {
    updateMarkers(markers, { centerAfterUpdate: true, shouldZoom: false });
  }, [markers, updateMarkers]);

  const mapProps = useMemo(
    () =>
      isDesktop
        ? {
            className: classes.mapContainerDesktop,
          }
        : {
            className: classes.mapContainer,
            style: { height: 30 * vh },
          },
    [isDesktop, vh, classes.mapContainer, classes.mapContainerDesktop]
  );

  return {
    markers,
    mapProps,
    onFirstFetch,
  };
};

export const useDriverDetails = ({ id }) => {
  const { dispatcher, driver, id: selfId, sessionVersion } = usePortalAccess();
  const allowed = dispatcher || (driver && String(id) === selfId);
  const {
    data: selectedDriver,
    isFetching: selectedDriverLoading,
    refetch,
    error,
  } = usePortalQuery(['driver-details', id, sessionVersion], () => getSelectedDriver(id), {
    enabled: allowed,
    interval: 15000,
  });
  const safeRefetch = useCallback(() => allowed ? refetch() : Promise.resolve(null), [allowed, refetch]);
  return {
    selectedDriver: allowed && !error ? selectedDriver : null,
    selectedDriverLoading,
    error,
    refetch: safeRefetch,
  };
};
